"use client";

import { createApi } from "@reduxjs/toolkit/query/react";
import baseQuery from "@/libs/shared/api/baseQuery";
import { resolveCustomerAvatarSrc } from "@/libs/shared/utils/avatar";

import {
  CreateCustomerResponse,
  CustomerGoal,
  CustomersCountModel,
  ListCustomersQuery,
  ListCustomersResponse,
  ListCustomersResponseItem,
} from "@/libs/customers/redux/customers.types";
import { objectToFormData } from "@/libs/shared/api/objectToFormData";
import { GetPayloadModel, GetResponseModel } from "@/libs/shared/api/api.types";
import { GroupModel } from "@/libs/customers/components/CustomerSummary/Groups/Groups.types";
import { settingsApi } from "@/libs/settings/redux/settings.api";
import {
  CreateCustomerBody,
  EditCustomerBody,
  GetCustomerResponse,
  GetSubscriptionResponse,
} from "./customers.types";

const tagTypes = [
  "Customer" as const,
  "Customers" as const,
  "InvitationCode" as const,
  "Assignments" as const,
  "Vod" as const,
  "Playlist" as const,
  "Groups" as const,
  "Note" as const,
  "Notes" as const,
  "Subscription" as const,
  "Subscriptions" as const,
  "SubscriptionNote" as const,
  "Events" as const,
  "LastWorkout" as const,
  "Workout" as const,
  "Workouts" as const,
  "ExecutedWorkout" as const,
  "ExecutedWorkouts" as const,
  "Equipment" as const,
  "History" as const,
  "Histories" as const,
  "TrainingPlans" as const,
  "Compositions" as const,
  "CompositionsProperty" as const,
  "CompositionsDates" as const,
  "Composition" as const,
  "CompositionsChart" as const,
  "Plicometries" as const,
  "PlicometriesProperty" as const,
  "PlicometriesDates" as const,
  "Plicometry" as const,
  "Healths" as const,
  "HealthsProperty" as const,
  "HealthsDates" as const,
  "Health" as const,
  "Measurements" as const,
  "MeasurementsProperty" as const,
  "MeasurementsDates" as const,
  "Measurement" as const,
  "PhotoProgress" as const,
  "PhotoProgressDates" as const,
  "Subscription" as const,
  "TrainingCardAttachments" as const,
];

export const invalidateCustomers = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) =>
        api.dispatch(customersApi.util.invalidateTags([tag])),
      );
    });
  };
};

export const customersApi = createApi({
  reducerPath: "customersApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getCustomers: builder.query<ListCustomersResponse, ListCustomersQuery>({
      query: ({
        perPage,
        page,
        orderBy,
        orderByDirection,
        status,
        groups,
        name,
        tc_end_lower_date,
        tc_end_upper_date,
      }) => ({
        url: `pt/customers?per_page=${perPage}&page=${page}&order_by=${orderBy}&order_by_direction=${orderByDirection}&status=${status}&groups=${groups}&name=${name}&tc_end_lower_date=${tc_end_lower_date}&tc_end_upper_date=${tc_end_upper_date}`,
      }),
      transformResponse: (response: any) => {
        const uniqueById = new Map<string, ListCustomersResponseItem>();

        (response.data || []).forEach((customer: ListCustomersResponseItem) => {
          uniqueById.set(customer.id.toString(), {
            ...customer,
            image: resolveCustomerAvatarSrc(customer?.image),
          });
        });

        return {
          ...response,
          // Deduplica i risultati nella singola pagina (BE a volte manda duplicati)
          data: Array.from(uniqueById.values()),
        };
      },
    }),
    getCustomer: builder.query<GetCustomerResponse, { id: string }>({
      query: ({ id }) => ({
        url: `pt/customers/${id}`,
      }),
      providesTags: ["Customer"],
    }),
    getCustomersCount: builder.query<CustomersCountModel, undefined>({
      query: () => ({
        url: `/pt/customers/counters`,
      }),
    }),
    getCustomersIds: builder.query<ListCustomersResponseItem[], undefined>({
      query: () => ({
        url: `/pt/customers/ids`,
      }),
      transformResponse: (response: number[]) => {
        const uniqueIds = Array.from(new Set(response));
        return uniqueIds.map((id: number) => ({
          id,
        })) as ListCustomersResponseItem[];
      },
    }),
    createCustomer: builder.mutation<
      CreateCustomerResponse,
      CreateCustomerBody
    >({
      query: (customer) => {
        const formData = objectToFormData(customer);
        return {
          url: "create_customer",
          method: "POST",
          body: formData,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(customersApi.util.invalidateTags(["Customers"]));
          dispatch(settingsApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Error handled by RTK Query
        }
      },
    }),
    editCustomer: builder.mutation<{ id: number }, EditCustomerBody>({
      query: (customer) => {
        const formData = objectToFormData(customer);
        return {
          url: `pt/customers/${customer?.id}`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateCustomers(["Customer"]),
    }),
    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `pt/customers/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            customersApi.util.invalidateTags(["Customers", "Customer"]),
          );
          dispatch(settingsApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Error handled by RTK Query
        }
      },
    }),
    editGoals: builder.mutation<void, { id: string; goals: CustomerGoal }>({
      query: ({ id, goals }) => {
        const formData = objectToFormData(goals);
        return {
          url: `pt/customers/${id}/goals`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateCustomers(["Customer"]),
    }),
    updateStatus: builder.mutation<void, { id: string; action: string }>({
      query: ({ id, action }) => ({
        url: `pt/customers/${id}/${action}`,
        method: "POST",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(customersApi.util.invalidateTags(["Customer"]));
          dispatch(settingsApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Error handled by RTK Query
        }
      },
    }),
    getInvitationCode: builder.query<{ code: string }, void>({
      query: () => ({
        url: "get_invitation_code",
      }),
      providesTags: ["InvitationCode"],
    }),
    resetInvitationCode: builder.mutation<void, string>({
      query: (invitation_code) => ({
        url: "reset_invitation_code",
        method: "POST",
        body: { invitation_code },
        formData: true,
      }),
      onQueryStarted: invalidateCustomers(["InvitationCode"]),
    }),
    resetPassword: builder.mutation<void, string>({
      query: (id) => ({
        url: `pt/customers/${id}/reset_password`,
        method: "POST",
      }),
    }),
    getGroups: builder.query<
      GetResponseModel<GroupModel>,
      Partial<GetPayloadModel>
    >({
      query: ({ perPage, page }: GetPayloadModel) => ({
        url: `pt/groups?per_page=${perPage}&page=${page}`,
      }),
    }),
    getSubscription: builder.query<GetSubscriptionResponse, void>({
      query: () => ({
        url: `pt/account/subscription`,
      }),
      providesTags: ["Subscription"],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useGetCustomersCountQuery,
  useGetCustomersIdsQuery,
  useCreateCustomerMutation,
  useGetInvitationCodeQuery,
  useResetInvitationCodeMutation,
  useGetCustomerQuery,
  useEditGoalsMutation,
  useEditCustomerMutation,
  useUpdateStatusMutation,
  useDeleteCustomerMutation,
  useResetPasswordMutation,
  useGetGroupsQuery,
} = customersApi;
